import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { FacetFilterId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFacets'
import React, { useCallback, useMemo, useRef } from 'react'

import { MultiSelectFilterField } from './MultiSelectFilterField'
import { Bar } from './styles'
import { TextFilterField } from './TextFilterField'
import type { CardFilterValues } from './types'
import { FILTER_FIELDS } from './types'

interface DocumentCardFilterBarProps {
  documents: DocumentTableRow[]
  facetOptions?: Partial<Record<FacetFilterId, string[]>>
  filters: CardFilterValues
  onFiltersChange: (filters: CardFilterValues) => void
  onFilterCommit?: () => void
}

export const DocumentCardFilterBar: React.FC<DocumentCardFilterBarProps> = ({
  documents,
  facetOptions,
  filters,
  onFiltersChange,
  onFilterCommit,
}) => {
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const uniqueValues = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const field of FILTER_FIELDS) {
      if (field.type !== 'multiselect') continue
      const serverOptions =
        field.id === 'vendor' || field.id === 'docType'
          ? facetOptions?.[field.id]
          : undefined
      if (serverOptions) {
        map[field.id] = serverOptions
        continue
      }
      const values = new Set<string>()
      for (const doc of documents) {
        const fieldValue = field.accessor(doc)
        if (fieldValue) values.add(fieldValue)
      }
      map[field.id] = Array.from(values).sort()
    }
    return map
  }, [documents, facetOptions])

  const updateFilter = useCallback(
    (fieldId: string, value: string | string[]) => {
      onFiltersChange({ ...filtersRef.current, [fieldId]: value })
    },
    [onFiltersChange]
  )

  return (
    <Bar>
      {FILTER_FIELDS.map((field) =>
        field.type === 'text' ? (
          <TextFilterField
            key={field.id}
            field={field}
            value={(filters[field.id] as string) ?? ''}
            onChange={(v) => updateFilter(field.id, v)}
            onFilterCommit={onFilterCommit}
          />
        ) : (
          <MultiSelectFilterField
            key={field.id}
            field={field}
            selected={(filters[field.id] as string[]) ?? []}
            options={uniqueValues[field.id] ?? []}
            onChange={(v) => updateFilter(field.id, v)}
            onFilterCommit={onFilterCommit}
          />
        )
      )}
    </Bar>
  )
}
